<template>
  <Transition name="premium-loader-fade">
    <div
      v-if="visible"
      class="premium-loader"
      :class="[themeClass, { 'premium-loader--fullscreen': fullscreen }]"
      role="status"
      aria-live="polite"
      :aria-label="message"
    >
      <div class="premium-loader__backdrop" />
      <div class="premium-loader__card">
        <div class="premium-loader__visual">
          <svg
            class="premium-loader__arcs"
            viewBox="0 0 88 88"
            aria-hidden="true"
          >
            <circle
              class="premium-loader__arc premium-loader__arc--1"
              cx="44"
              cy="44"
              r="36"
            />
            <circle
              class="premium-loader__arc premium-loader__arc--2"
              cx="44"
              cy="44"
              r="27"
            />
            <circle
              class="premium-loader__arc premium-loader__arc--3"
              cx="44"
              cy="44"
              r="18"
            />
          </svg>
          <span class="premium-loader__icon-wrap">
            <v-icon :icon="icon" />
          </span>
        </div>

        <h3 class="premium-loader__title">{{ message }}</h3>
        <p v-if="hint" class="premium-loader__hint">{{ hint }}</p>

        <div
          class="premium-loader__progress-block"
          role="progressbar"
          :aria-valuenow="roundedProgress"
          aria-valuemin="0"
          aria-valuemax="100"
        >
          <span class="premium-loader__percent">{{ roundedProgress }}%</span>
          <div class="premium-loader__progress">
            <span
              class="premium-loader__progress-bar"
              :class="{ 'premium-loader__progress-bar--determinate': useDeterminateBar }"
              :style="determinateBarStyle"
            />
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script>
import "@/assets/styles/premium-loader.css";

export default {
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    progress: {
      type: Number,
      default: null,
    },
    currentTheme: {
      type: String,
      default: "light",
    },
    icon: {
      type: String,
      default: "mdi-database-sync-outline",
    },
    message: {
      type: String,
      default: "Loading data…",
    },
    hint: {
      type: String,
      default: "Please wait while we fetch your data",
    },
    fullscreen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      visible: false,
      simulatedProgress: 0,
      progressTimer: null,
      hideTimer: null,
    };
  },
  computed: {
    themeClass() {
      return this.currentTheme === "dark"
        ? "premium-loader--dark"
        : "premium-loader--light";
    },
    displayProgress() {
      if (this.progress !== null && this.progress !== undefined) {
        return Math.min(100, Math.max(0, this.progress));
      }
      return this.simulatedProgress;
    },
    roundedProgress() {
      return Math.round(this.displayProgress);
    },
    useDeterminateBar() {
      return this.progress !== null && this.progress !== undefined
        ? true
        : this.simulatedProgress > 0;
    },
    determinateBarStyle() {
      if (!this.useDeterminateBar) return {};
      return {
        width: `${this.displayProgress}%`,
      };
    },
  },
  watch: {
    loading: {
      immediate: true,
      handler(isLoading) {
        if (isLoading) {
          this.showLoader();
        } else {
          this.hideLoader();
        }
      },
    },
    progress(value) {
      if (value !== null && value !== undefined && this.loading) {
        this.simulatedProgress = Math.min(100, Math.max(0, value));
      }
    },
  },
  beforeUnmount() {
    this.clearTimers();
  },
  methods: {
    showLoader() {
      this.clearTimers();
      this.visible = true;
      this.simulatedProgress = 0;
      if (this.progress === null || this.progress === undefined) {
        this.startSimulatedProgress();
      } else {
        this.simulatedProgress = this.displayProgress;
      }
    },
    hideLoader() {
      this.clearProgressTimer();
      if (this.progress === null || this.progress === undefined) {
        this.simulatedProgress = 100;
      }
      this.hideTimer = setTimeout(() => {
        this.visible = false;
        this.simulatedProgress = 0;
      }, 320);
    },
    startSimulatedProgress() {
      this.clearProgressTimer();
      this.progressTimer = setInterval(() => {
        if (this.progress !== null && this.progress !== undefined) return;
        const cap = 92;
        const remaining = cap - this.simulatedProgress;
        const step = Math.max(0.35, remaining * 0.07);
        this.simulatedProgress = Math.min(cap, this.simulatedProgress + step);
      }, 110);
    },
    clearProgressTimer() {
      if (this.progressTimer) {
        clearInterval(this.progressTimer);
        this.progressTimer = null;
      }
    },
    clearHideTimer() {
      if (this.hideTimer) {
        clearTimeout(this.hideTimer);
        this.hideTimer = null;
      }
    },
    clearTimers() {
      this.clearProgressTimer();
      this.clearHideTimer();
    },
  },
};
</script>
